import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

import { MessageService } from './message.service';
import { OptionsService } from './options.service';
import { AnswerService } from './answer.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class CindiQuestionnaireService {
  private personalDataLoaded = false;
  personalData!: any;

  constructor(
    private messageService: MessageService,
    private optionsService: OptionsService,
    private answerService: AnswerService,
    private authService: AuthService,
  ) {
    this.answerService.personalData$.subscribe((data) => {
      this.personalData = data;
    });
  }

  async initCindiQuestionnaire(idPessoa: number) {
    await this.answerService.createUserQuestionnaire(idPessoa);
    await this.messageService.addLocalBotMsgToChat(1);
    await this.messageService.addLocalBotMsgToChat(2);
    await this.messageService.addLocalBotMsgToChat(3);
    await this.optionsService.generateMessageWithOptions(4);
  }

  async continueQuestionsFlow(nextMessageId: number) {
    const hasOtherSymptoms = this.answerService.hasOtherSymptoms;
    const hasLowerBackPain = this.answerService.hasLowerBackPain;

    if (!hasLowerBackPain && nextMessageId === 5) {
      await this.handleUserExit();
      return;
    }

    if (hasLowerBackPain && nextMessageId === 5) {
      await this.optionsService.generateMessageWithOptions(5);
      await this.optionsService.generateMessageWithOptions(6);
      await this.messageService.addLocalBotMsgToChat(7);
      await this.optionsService.generateMessageWithOptions(8);
      return;
    }

    if (this.isInRange(nextMessageId, 9, 17)) {
      if (!this.personalDataLoaded) {
        await this.getPersonalData();
        this.personalDataLoaded = true;
      }
      await this.handlePersonalDataQuestions();
      return;
    }

    if (this.isInRange(nextMessageId, 18, 32)) {
      await this.handleStartback(nextMessageId);
    }

    if (nextMessageId === 40) {
      return hasOtherSymptoms
        ? this.handleSymptomFlow()
        : this.finishQuestionnaire();
    }
  }

  private async handleUserExit() {
    await this.messageService.addLocalBotMsgToChat(49);
    await this.messageService.addLocalBotMsgToChat(50);
    await this.messageService.addLocalBotMsgToChat(51);
    await this.messageService.addLocalBotMsgToChat(52);
    await this.messageService.addLocalBotMsgToChat(53);
    await this.optionsService.generateMessageWithOptions(39);
  }

  private isInRange(id: number, min: number, max: number): boolean {
    return id >= min && id <= max;
  }

  private async getPersonalData() {
    const userId = this.authService.getUserId();
    await this.answerService.loadPersonalDataFromApi(userId);
  }

  private async handlePersonalDataQuestions() {
    const hasRedFlags = this.answerService.hasRedFlags;
    const hasAcutePain = this.answerService.hasAcutePain;
    const hasSecondaryDisease = this.answerService.hasSecondaryDisease;

    const fieldsInOrder = [
      { value: this.personalData.pais, questionId: 9 },
      { value: this.personalData.cidadeResidencia, questionId: 10 },
      { value: this.personalData.estadoCivil, questionId: 11 },
      { value: this.personalData.grauEscolaridade, questionId: 12 },
      { value: this.personalData.classificacaoEtnicoRacial, questionId: 13 },
      { value: this.personalData.cargo, questionId: 14 },
      { value: this.personalData.area, questionId: 15 },
      { value: this.personalData.turno, questionId: 16 },
    ];

    if (hasRedFlags || hasAcutePain || hasSecondaryDisease) {
      await this.handleRedFlags();
      return;
    }

    const next = fieldsInOrder.find(
      (item) => item.value === null || item.value === '',
    );

    if (next && next.questionId == 16) {
      await this.optionsService.generateMessageWithOptions(
        next.questionId,
        undefined,
        1,
        2,
      );
    } else if (next && next.questionId !== 16) {
      await this.optionsService.generateMessageWithOptions(next.questionId);
    } else {
      await this.messageService.addLocalBotMsgToChat(17);
      await this.optionsService.generateMessageWithOptions(18);
    }
  }

  private async handleStartback(id: number) {
    if (id == 32) {
      await this.messageService.addLocalBotMsgToChat(32);
      await this.messageService.addLocalBotMsgToChat(33);
      await this.messageService.addLocalBotMsgToChat(34);
      await this.messageService.addLocalBotMsgToChat(35, 8000);
      await this.messageService.addLocalBotMsgToChat(36, 4000);
      await this.messageService.addLocalBotMsgToChat(37, 8000);
      await this.messageService.addLocalBotMsgToChat(38, 4000);
      await this.optionsService.generateMessageWithOptions(
        39,
        undefined,
        undefined,
        undefined,
        8000,
      );
    } else {
      await this.optionsService.generateMessageWithOptions(id);
    }
  }

  private async handleRedFlags() {
    await this.messageService.addLocalBotMsgToChat(47);
    await this.messageService.addLocalBotMsgToChat(48);
    await this.optionsService.generateMessageWithOptions(39);
  }

  private async handleSymptomFlow() {
    await this.optionsService.generateMessageWithOptions(40);
    await this.messageService.addLocalBotMsgToChat(41);
    await this.messageService.addLocalBotMsgToChat(42);
    await this.messageService.addLocalBotMsgToChat(43);
    await this.finishQuestionnaire();
  }

  async finishQuestionnaire() {
    await this.optionsService.generateMessageWithOptions(44);
    await this.messageService.addLocalBotMsgToChat(45);
    await this.messageService.addLocalBotMsgToChat(46);

    await this.answerService.saveAnswersByQuestionnaire();
    await this.answerService.setUserQuestionnaireStatus('Completo');
    this.logout();
  }

  logout() {
    setTimeout(() => {
      sessionStorage.removeItem('token');
      window.location.reload();
    }, 5000);
  }
}
